<template>
  <div>
    <aside
      class="store-selector modal js-modal"
      id="storeSelectorModal"
      aria-hidden="true"
      aria-labelledby="storeSelector"
      role="dialog"
    >
      <div class="store-selector__inner modal__inner">
        <header class="store-selector__header modal__header">
          <button
            class="store-selector__close modal__close btn-icon js-modal-close"
            id="modalCloseButton"
            type="button"
            title="Close modal"
            :tabindex="0"
          >
            <i class="icon icon--close"></i>
            <span class="store-selector__close-text">
              close
            </span>
          </button>
        </header>
        <div class="store-selector__content modal__content">
          <div class="store-selector__content-header">
            <h3
              class="store-selector__heading"
              id="storeSelector"
            >
              Find a store near you
            </h3>
            <div class="store-selector__content-search">
              <form
                class="store-selector__form"
                id="storeSelectorForm"
                @submit.prevent="handleFormSubmit"
              >
                <input
                  class="js-hidden-item-id"
                  type="hidden"
                  value=""
                >
                <input
                  class="js-hidden-item-properties"
                  type="hidden"
                  value=""
                >
                <fieldset class="store-selector__fieldset">
                  <legend class="screenreader">Find a store near you</legend>
                  <ul class="form-list">
                    <li class="form-item">
                      <div class="input-placeholder">
                        <input
                          class="input zip-code"
                          id="zip_code"
                          type="text"
                          placeholder="Zip Code"
                          @keydown.enter.prevent="handleFormSubmit"
                        />
                        <label for="zip_code">
                          Zip Code
                        </label>
                      </div>
                    </li>
                    <li class="form-item">
                      <div
                        class="store-selector__dropdown-menu js-store-selector-dropdown-menu"
                        id="js-store-selector-dropdown-menu"
                      >
                        <h2 class="dropdown-heading">
                          <button
                            class="js-store-selector-trigger options-label"
                            id="dropdown-btn-store-selector"
                            type="button"
                            :tabindex="0"
                            aria-label="Open dropdown menu"
                            aria-controls="storeSelectorDropdownWrapper"
                            aria-expanded="false"
                          >
                            <span class="options-label__select js-store-selector-label">
                              Radius
                            </span>
                            <span class="options-label__text js-store-selector-trigger-text">
                              Select
                            </span>
                            <span class="options-label__icon icon icon--chevron-down"></span>
                          </button>
                        </h2>
                        <div
                          class="store-selector__dropdown-wrapper"
                          id="storeSelectorDropdownWrapper"
                          :tabindex="0"
                          aria-labelledby="dropdown-btn-store-selector"
                          role="region"
                        >
                          <ul class="store-selector__dropdown-list list-reset">
                            <li
                              v-for="(radius, index) in radiusDropdown"
                              :key="radius.value"
                              class="dropdown-list__item js-dropdown-list-item"
                              :class="{ 'is-active': index === 0 }"
                              :data-option="radius.value"
                              :data-text="radius.text"
                              :data-value="radius.value"
                            >
                              <button
                                class="dropdown-list__item-btn"
                                type="button"
                                :aria-pressed="index === 0 ? 'true' : 'false'"
                              >
                                <span class="dropdown-list__item-btn-text">
                                  {{ radius.text }}
                                </span>
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>
                    <li
                      class="store-selector__form-btn form-item js-store-selector-search-btn"
                      id="js-store-selector-search-btn"
                    >
                      <button
                        class="btn btn--primary btn--full-width"
                        type="button"
                        @click="setNearestStoreData"
                      >
                        Search
                      </button>
                    </li>
                  </ul>
                </fieldset>
              </form>
            </div>
          </div>
          <div class="store-selector__content-stores">
            <div class="store-selector__content-result-text">
              <p
                v-if="visibleStoreCount"
                class="store-selector__content-result-count js-store-result-count"
              >
                {{ visibleStoreCount }} location<span v-if="visibleStoreCount > 1">s</span>
              </p>
            </div>
            <div
              v-if="visibleStoreCount === 0"
              class="store-selector__content-result-list"
            >
              <p>No stores available</p>
            </div>
            <div v-else class="store-selector__content-result-list">
              <SelectedStore
                v-for="store in visibleStores"
                :key="store.name"
                :store="store"
                :storesWithDistance="storesWithDistance"
                @set-store-data="handleStoreData"
              ></SelectedStore>
            </div>
          </div>
        </div>
      </div>
    </aside>
    <div
      class="store-selector__overlay modal-overlay"
      id="storeSelectorModalOverlay"
    ></div>
  </div>
</template>
<script>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
} from 'vue';
import storeSelector from '../../lib/store-selector';
import trapFocus from '../../lib/trap-focus';
import getCachedGeolocation from '../../lib/get-cached-geolocation';
import useCart from '../composables/cart-actions';
import useDelivery from '../composables/delivery-estimator';
import { getCollectionPickupLocationIds, isStoreEligibleForPickup } from '../../lib/pickup-eligibility';
import SelectedStore from './SelectedStore';

export default {
  name: 'StoreSelector',
  components: {
    SelectedStore,
  },
  props: {
    // When true (collection-page Same Day Pickup context), the store list is
    // additionally filtered to locations with pickup-eligible products in the
    // current collection. Default false keeps cart drop-off and every other
    // context unchanged — all drop-off stores remain selectable.
    pickupContext: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const {
      addDropOffStore,
    } = useCart();

    const {
      updateZipcode,
    } = useDelivery();

    const radiusDropdown = JSON.parse(SDG.Data.radiusDropdownOptions || null) || [
      {"value": "5", "text": "5 miles"},
      {"value": "10", "text": "10 miles"},
      {"value": "25", "text": "25 miles"},
      {"value": "50", "text": "50 miles"},
      {"value": "all", "text": "Show all"}
    ];
    const widgetTag = SDG.Data.stockistWidgetTag;
    const { mapBoxApiKey } = SDG.Data;
    const storage = window.sessionStorage;
    const stores = ref([]);
    const storeCount = ref(0);
    const storesWithDistance = ref(null);
    const packagingTypeKey = (typeof SDG !== 'undefined' && SDG?.Data?.cart?.packaging_type_property_label) || 'Packaging Type';
    const shipToUsPropertyNames = [packagingTypeKey, 'shipToUs'];

    // Pickup-eligibility filtering (collection-page Same Day Pickup context only).
    const eligibleLocationIds = ref(null);
    const refreshEligibleLocationIds = () => {
      if (props.pickupContext) {
        eligibleLocationIds.value = getCollectionPickupLocationIds();
      }
    };

    // In pickup context, hide stores with no pickup-eligible products in the
    // current collection so a selection can never produce a blank grid. Outside
    // pickup context, show the full drop-off list unchanged.
    const visibleStores = computed(() => {
      if (!props.pickupContext) {
        return stores.value;
      }
      return stores.value.filter(
        (store) => isStoreEligibleForPickup(store, eligibleLocationIds.value),
      );
    });
    const visibleStoreCount = computed(() => visibleStores.value.length);

    onMounted(async () => {
      refreshEligibleLocationIds();
      window.addEventListener('productsLoaded', refreshEligibleLocationIds);
      const coords = await getCachedGeolocation();
      setDefaultDropOffStores(coords ? { coords } : null);
      storeSelector();
    });

    onUnmounted(() => {
      window.removeEventListener('productsLoaded', refreshEligibleLocationIds);
    });

    const handleStoreData = (newStore) => {
      if (newStore) {
        const itemId = document.querySelector('.js-hidden-item-id');
        const itemIdValue = itemId.value || null;
        const itemProperties = document.querySelector('.js-hidden-item-properties');
        const itemPropertiesString = itemProperties?.value || "";

        let itemPropertiesValue = null;
        if (itemPropertiesString && itemPropertiesString.trim() !== "") {
          try {
            itemPropertiesValue = JSON.parse(itemPropertiesString);
          } catch (error) {
            console.warn("Error parsing item properties:", error);
            itemPropertiesValue = null;
          }
        }

        const itemPropertiesArray = itemPropertiesValue
          ? Object.entries(itemPropertiesValue)
          : [];

        const filteredItemPropertiesArray = itemPropertiesArray.filter(
          (property) => !shipToUsPropertyNames.includes(property[0]),
        );

        const filteredItemProperties = {};
        filteredItemPropertiesArray.forEach(
          (array) => {
            const key = array[0];
            const value = array[1];
            filteredItemProperties[key] = value;
          },
        );

        if (itemIdValue != null) {
          // newStore is now the full store object; extract name for addDropOffStore
          const storeName = newStore.name || newStore;
          addDropOffStore(itemIdValue, filteredItemProperties, storeName);

          // Emit the full store object (with custom_fields) for pickup availability
          const newStoreData = {
            ...newStore,
            storeName,
            itemId: itemIdValue,
          };

          context.emit('setDropOffStore', newStoreData);
        }
      }
    };

    const storeSelectorBtn = () => {
      const $body = document.body;
      __.addClass($body, 'is-store-selector-exposed');
      const $storeSelectorModal = document.getElementById('storeSelectorModal');

      setTimeout(() => {
        trapFocus($storeSelectorModal);
      }, 150);
    };

    /**
     * Set the Stockist store data
     */
    const setDefaultDropOffStores = async (geolocationPosition = null) => {
      let coords = null;

      if (storage) storage.setItem('dropOffStores', null);
      const ipStackData = storage.getItem('ipStackData')
        ? JSON.parse(storage.getItem('ipStackData'))
        : null;

      if (geolocationPosition !== null) {
        coords = {
          latitude: geolocationPosition.coords.latitude,
          longitude: geolocationPosition.coords.longitude,
        };
      }

      if (coords === null) {
        // If no Geolocation data set coords from IPStack data
        const { latitude, longitude } = ipStackData || {};
        coords = { latitude, longitude };
      }

      const { latitude, longitude } = coords;

      const stockistEndpoint = latitude && longitude
        ? `https://stockist.co/api/v1/${widgetTag}/locations/search?latitude=${latitude}&longitude=${longitude}`
        : `https://stockist.co/api/v1/${widgetTag}/locations/all`;

      const results = await fetch(stockistEndpoint)
        .then((searchResult) => searchResult.json())
        .catch((err) => console.error(err));

      const filteredResults = (latitude && longitude ? results.locations : results)
        .filter((result) => (
          result.custom_fields.find((field) => (
            field.name.toLowerCase() === 'drop off in store' && field.value.toLowerCase() === 'true'
          ))
        ))
        .filter((store) => {
          const badgeField = store.custom_fields?.find(
            (field) => field.name.includes('Badge')
          );
          return badgeField?.value !== 'Coming Soon!';
        });

      if (storage) storage.setItem('dropOffStores', JSON.stringify(filteredResults));
      stores.value = filteredResults;
      storeCount.value = filteredResults.length;
    };

    /**
     * Display the Store Selector nearest stores
     */
    function displayNearestDropOffStores(storesData, distance) {
      if (!distance || distance === 'all') {
        stores.value = storesData;
        storeCount.value = storesData.length;
      } else {
        const filteredStoresData = storesData.filter(
          (singleDropOffStore) => singleDropOffStore.distance <= distance,
        );

        if (filteredStoresData && filteredStoresData.length > 0) {
          stores.value = filteredStoresData;
          storeCount.value = filteredStoresData.length;
        } else {
          stores.value = [];
          storeCount.value = 0;
        }
      }
    }

    /**
     * Set the Stockist store data
     */
    const setNearestStoreData = async () => {
      if (storage) storage.setItem('dropOffStores', null);

      const $form = document.getElementById('storeSelectorForm');
      const zipCode = $form.querySelector('.zip-code').value || null;
      const selectOption = $form.querySelector('.js-store-selector-trigger-text').dataset.value || 0;

      if (zipCode) {
        // Update the PDP delivery estimate with the new zip code
        updateZipcode(zipCode);
        
        // Dispatch event to notify enhanced-delivery-estimate.js
        window.dispatchEvent(new CustomEvent('SDG-zip-updated', {
          detail: { zip: zipCode }
        }));

        // Set Mapbox data
        await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${zipCode}.json?access_token=${mapBoxApiKey}`)
          .then((resp) => resp.json())
          .then(async (data) => {
            // Get Stockist data
            const results = data.features;

            if (results.length > 0) {
              const result = results[0].center;
              const latitude = result[1];
              const longitude = result[0];

              await fetch(`https://stockist.co/api/v1/${widgetTag}/locations/search?latitude=${latitude}&longitude=${longitude}`)
                .then((resp) => resp.json())
                .then((searchResult) => {
                  const allStores = searchResult.locations;
                  const filteredStores = allStores
                    .filter((store) => {
                      const customFields = store.custom_fields;
                      return customFields.find((field) => field.name.toLowerCase() === 'drop off in store' && field.value.toLowerCase() === 'true');
                    })
                    .filter((store) => {
                      const badgeField = store.custom_fields?.find(
                        (field) => field.name.includes('Badge')
                      );
                      return badgeField?.value !== 'Coming Soon!';
                    });

                  if (storage) storage.setItem('dropOffStores', JSON.stringify(filteredStores));

                  stores.value = filteredStores;
                  storesWithDistance.value = filteredStores;
                  displayNearestDropOffStores(filteredStores, parseInt(selectOption, 10));
                })
                .catch((err) => console.error(err));
            } else {
              stores.value = [];
              storeCount.value = 0;
            }
          })
          .catch((err) => console.error(err));
      } else {
        setDefaultDropOffStores();
      }
    };

    /**
     * Handle form submission (triggered by Enter key or Search button)
     */
    const handleFormSubmit = () => {
      setNearestStoreData();
    };

    return {
      storeSelectorBtn,
      radiusDropdown,
      stores,
      storeCount,
      visibleStores,
      visibleStoreCount,
      storesWithDistance,
      setNearestStoreData,
      handleStoreData,
      handleFormSubmit,
    };
  },
};
</script>
